import React, { useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'

import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Stepper,
    Step,
    StepLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    makeStyles,
    Theme,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import {
    ArrowBack as ArrowLeft,
    CheckCircle,
    Info as InfoIcon,
} from '@material-ui/icons'

import { Routes } from '../../../navigation/config/routes'
import { SpreadsheetUploadCard } from '../../../components/DropFiles'
import {
    parseSpreadsheet,
    autoDetectColumnMapping,
    getHeaderRow,
    getRowsFromIndex,
    ColumnMapping,
} from '../../../utils/spreadsheetParser'
import { useApartmentProspects } from '../../../hooks/Outreach/useApartmentProspects'
import { useUser } from '../../../hooks/useUser'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        maxWidth: '1024px',
        margin: '0 auto',
        padding: theme.spacing(3),
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: '#6b7280',
        fontSize: '1rem',
    },
    backButton: {
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '0.875rem',
        fontWeight: 500,
        borderColor: '#d1d5db',
        color: '#6b7280',
        '&:hover': {
            borderColor: '#3b82f6',
            color: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
    },
    stepper: {
        marginBottom: theme.spacing(4),
        padding: theme.spacing(2),
    },
    stepContent: {
        padding: theme.spacing(3),
        minHeight: 400,
    },
    formField: {
        marginBottom: theme.spacing(3),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    previewTable: {
        marginTop: theme.spacing(2),
        maxHeight: 400,
        overflow: 'auto',
    },
    tableHeader: {
        backgroundColor: '#f3f4f6',
        fontWeight: 600,
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(4),
        paddingTop: theme.spacing(3),
        borderTop: '1px solid #e2e8f0',
    },
    gradientButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        borderRadius: '8px',
        padding: '14px 32px',
        fontSize: '1rem',
        fontWeight: 600,
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
        '&:disabled': {
            background: '#9ca3af',
            color: 'white',
        },
    },
    rowSelector: {
        marginBottom: theme.spacing(2),
    },
    mappingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    mappingLabel: {
        minWidth: 150,
        fontWeight: 500,
        color: '#1f2937',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
    },
    errorText: {
        color: '#ef4444',
        marginTop: theme.spacing(1),
    },
}))

type UploadStep = 'upload' | 'header' | 'mapping' | 'confirmation'

export const SpreadsheetUploadPage = () => {
    const classes = useStyles()
    const { schoolId: schoolIdParam } = useParams<{
        schoolId?: string
    }>()
    const history = useHistory()
    const schoolId = schoolIdParam ? Number(schoolIdParam) : null
    const isApartmentPoolUpload =
        schoolId != null && !Number.isNaN(schoolId) && schoolId > 0

    const { workspaceUser } = useUser()
    const apartmentId =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.id
            : undefined

    const {
        uploadSpreadsheet: uploadApartmentSpreadsheet,
    } = useApartmentProspects(apartmentId)

    const [activeStep, setActiveStep] = useState<number>(0)
    const [uploadedFile, setUploadedFile] = useState<File | null>(null)
    const [parsedData, setParsedData] = useState<string[][]>([])
    const [headerRowIndex, setHeaderRowIndex] = useState<number>(0)
    const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
        name: null,
        first_name_header: null,
        last_name_header: null,
        linkedinProfile: null,
        email: null,
        phone: null,
    })
    const [isNameSplit, setIsNameSplit] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const steps: UploadStep[] = ['upload', 'header', 'mapping', 'confirmation']

    const handleFileDrop = async (
        files: File[],
        rejectedFiles?: Array<{
            file: File
            errors: Array<{ code: string; message: string }>
        }>,
    ) => {
        // Handle rejected files
        if (rejectedFiles && rejectedFiles.length > 0) {
            setError('Invalid file type. Please upload a CSV or XLSX file.')
            return
        }

        if (files.length > 0) {
            const file = files[0]

            // Validate file type
            const fileName = file.name.toLowerCase()
            const fileExtension = fileName.split('.').pop()
            if (!['csv', 'xlsx', 'xls'].includes(fileExtension || '')) {
                setError('Invalid file type. Please upload a CSV or XLSX file.')
                return
            }

            setUploadedFile(file)
            setLoading(true)
            setError(null)

            try {
                const data = await parseSpreadsheet(file)
                if (data.length === 0) {
                    throw new Error('File appears to be empty')
                }
                setParsedData(data)
                setHeaderRowIndex(0) // Default to first row

                // Auto-detect column mapping on initial load
                const headers = getHeaderRow(data, 0)
                const detectedMapping = autoDetectColumnMapping(headers)
                // Check if both first and last name are detected
                if (
                    detectedMapping.first_name_header !== null &&
                    detectedMapping.last_name_header !== null
                ) {
                    setIsNameSplit(true)
                }
                setColumnMapping(detectedMapping)

                setActiveStep(1) // Move to header selection step
            } catch (err: any) {
                console.error('Error parsing spreadsheet:', err)
                setError(err.message || 'Failed to parse file')
                setUploadedFile(null)
            } finally {
                setLoading(false)
            }
        }
    }

    const handleHeaderRowChange = (newIndex: number) => {
        setHeaderRowIndex(newIndex)
        // Auto-detect column mapping when header row changes
        const headers = getHeaderRow(parsedData, newIndex)
        const detectedMapping = autoDetectColumnMapping(headers)
        // Check if both first and last name are detected
        if (
            detectedMapping.first_name_header !== null &&
            detectedMapping.last_name_header !== null
        ) {
            setIsNameSplit(true)
        }
        setColumnMapping(detectedMapping)
    }

    const handleColumnMappingChange = (
        field: keyof ColumnMapping,
        columnIndex: number | null,
    ) => {
        setColumnMapping({
            ...columnMapping,
            [field]: columnIndex,
        })
    }

    const handleNameSplitToggle = () => {
        const newIsNameSplit = !isNameSplit
        setIsNameSplit(newIsNameSplit)
        if (newIsNameSplit) {
            // Clear the name mapping when switching to split
            setColumnMapping({
                ...columnMapping,
                name: null,
            })
        } else {
            // Clear first and last name mappings when switching to single
            setColumnMapping({
                ...columnMapping,
                first_name_header: null,
                last_name_header: null,
            })
        }
    }

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1)
        }
    }

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1)
        }
    }

    const handleUpload = async () => {
        if (!uploadedFile) return
        if (isApartmentPoolUpload) {
            if (schoolId == null) return
            setLoading(true)
            try {
                await uploadApartmentSpreadsheet.mutateAsync({
                    schoolId,
                    file: uploadedFile,
                    headerRow: headerRowIndex,
                    columnMapping,
                })
                history.push(Routes.manage.leasingSeason.root)
            } catch (err) {
                // Error is already handled by the mutation
            } finally {
                setLoading(false)
            }
            return
        }
    }

    const getHeaders = (): string[] => {
        return getHeaderRow(parsedData, headerRowIndex)
    }

    const getPreviewRows = (): string[][] => {
        return getRowsFromIndex(parsedData, headerRowIndex + 1, 5)
    }

    const renderUploadStep = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                Upload Spreadsheet
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
                Upload a CSV or XLSX file containing your prospect data. The
                file should have columns for Name, Email, Phone, and/or LinkedIn
                Profile. Extra columns are kept as custom fields.
            </Typography>
            <SpreadsheetUploadCard
                onDrop={(acceptedFiles, rejectedFiles) => {
                    handleFileDrop(acceptedFiles, rejectedFiles).catch(
                        (err) => {
                            console.error('Error in handleFileDrop:', err)
                        },
                    )
                }}
                uploadedFile={uploadedFile}
                onRemove={() => {
                    setUploadedFile(null)
                    setParsedData([])
                    setActiveStep(0)
                    setError(null)
                }}
                title="Spreadsheet Upload"
                description="Upload targets/prospects (.csv or .xlsx)"
            />
            {loading && (
                <Box className={classes.loadingContainer}>
                    <CircularProgress />
                </Box>
            )}
            {error && (
                <Typography className={classes.errorText}>{error}</Typography>
            )}
        </Box>
    )

    const renderHeaderStep = () => {
        const headers = getHeaders()
        const previewRows = getRowsFromIndex(parsedData, 0, 10)

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Select Header Row
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Click on the row that contains your column headers. We have
                    detected row 1 as the default.
                </Typography>

                <Typography
                    variant="subtitle2"
                    gutterBottom
                    style={{ marginTop: 16 }}
                >
                    Preview (first 10 rows - click a row to select it as
                    headers):
                </Typography>
                <TableContainer
                    component={Paper}
                    className={classes.previewTable}
                >
                    <Table size="small">
                        <TableBody>
                            {previewRows.map((row, rowIndex) => (
                                <TableRow
                                    key={rowIndex}
                                    onClick={() =>
                                        handleHeaderRowChange(rowIndex)
                                    }
                                    style={{
                                        backgroundColor:
                                            rowIndex === headerRowIndex
                                                ? 'rgba(59, 130, 246, 0.15)'
                                                : 'transparent',
                                        cursor: 'pointer',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (rowIndex === headerRowIndex) {
                                            e.currentTarget.style.backgroundColor =
                                                'rgba(59, 130, 246, 0.2)'
                                        } else {
                                            e.currentTarget.style.backgroundColor =
                                                'rgba(59, 130, 246, 0.05)'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (rowIndex === headerRowIndex) {
                                            e.currentTarget.style.backgroundColor =
                                                'rgba(59, 130, 246, 0.15)'
                                        } else {
                                            e.currentTarget.style.backgroundColor =
                                                'transparent'
                                        }
                                    }}
                                >
                                    <TableCell
                                        style={{
                                            minWidth: 80,
                                            fontWeight:
                                                rowIndex === headerRowIndex
                                                    ? 600
                                                    : 500,
                                            color:
                                                rowIndex === headerRowIndex
                                                    ? '#3b82f6'
                                                    : '#6b7280',
                                            borderRight: '1px solid #e2e8f0',
                                        }}
                                    >
                                        Row {rowIndex + 1}
                                    </TableCell>
                                    {row.map((cell, cellIndex) => (
                                        <TableCell key={cellIndex}>
                                            {cell || ''}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography
                    variant="subtitle2"
                    gutterBottom
                    style={{ marginTop: 24 }}
                >
                    Detected Headers from Row {headerRowIndex + 1}:
                </Typography>
                <Box
                    style={{
                        padding: 16,
                        backgroundColor: '#f3f4f6',
                        borderRadius: 8,
                        marginTop: 8,
                    }}
                >
                    {headers.length > 0 ? (
                        <Typography variant="body2">
                            {headers.join(' • ')}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No headers detected
                        </Typography>
                    )}
                </Box>
            </Box>
        )
    }

    const renderMappingStep = () => {
        const headers = getHeaders()

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Map Columns
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Map your spreadsheet columns to our enrichment fields. None
                    of these are required - we will generate any missing data
                    through our enrichment process. We have auto-detected likely
                    matches, but you can adjust them as needed.
                </Typography>

                {/* Name Mapping - with split option */}
                <Box
                    className={classes.mappingRow}
                    style={{ marginBottom: 16 }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <Typography className={classes.mappingLabel}>
                            Name:
                        </Typography>
                        <Tooltip title="If your name column is split into first and last name columns, click this button">
                            <IconButton
                                size="small"
                                onClick={handleNameSplitToggle}
                                style={{
                                    color: isNameSplit ? '#3b82f6' : '#6b7280',
                                    padding: '4px 8px',
                                }}
                            >
                                <InfoIcon
                                    fontSize="small"
                                    style={{ marginRight: 4 }}
                                />
                                <Typography
                                    variant="caption"
                                    style={{
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                    }}
                                >
                                    My Name column is split into first and last
                                    name
                                </Typography>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {isNameSplit ? (
                    <>
                        <Box className={classes.mappingRow}>
                            <Typography className={classes.mappingLabel}>
                                First Name:
                            </Typography>
                            <FormControl
                                variant="outlined"
                                style={{ minWidth: 250 }}
                            >
                                <InputLabel>
                                    Select First Name Column
                                </InputLabel>
                                <Select
                                    value={
                                        columnMapping.first_name_header !== null
                                            ? columnMapping.first_name_header
                                            : ''
                                    }
                                    onChange={(e) =>
                                        handleColumnMappingChange(
                                            'first_name_header',
                                            e.target.value === ''
                                                ? null
                                                : (e.target.value as number),
                                        )
                                    }
                                    label="Select First Name Column"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {headers.map((header, index) => (
                                        <MenuItem key={index} value={index}>
                                            {header || `Column ${index + 1}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box className={classes.mappingRow}>
                            <Typography className={classes.mappingLabel}>
                                Last Name:
                            </Typography>
                            <FormControl
                                variant="outlined"
                                style={{ minWidth: 250 }}
                            >
                                <InputLabel>Select Last Name Column</InputLabel>
                                <Select
                                    value={
                                        columnMapping.last_name_header !== null
                                            ? columnMapping.last_name_header
                                            : ''
                                    }
                                    onChange={(e) =>
                                        handleColumnMappingChange(
                                            'last_name_header',
                                            e.target.value === ''
                                                ? null
                                                : (e.target.value as number),
                                        )
                                    }
                                    label="Select Last Name Column"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {headers.map((header, index) => (
                                        <MenuItem key={index} value={index}>
                                            {header || `Column ${index + 1}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </>
                ) : (
                    <Box className={classes.mappingRow}>
                        <Typography className={classes.mappingLabel}>
                            Name:
                        </Typography>
                        <FormControl
                            variant="outlined"
                            style={{ minWidth: 250 }}
                        >
                            <InputLabel>Select Name Column</InputLabel>
                            <Select
                                value={
                                    columnMapping.name !== null
                                        ? columnMapping.name
                                        : ''
                                }
                                onChange={(e) =>
                                    handleColumnMappingChange(
                                        'name',
                                        e.target.value === ''
                                            ? null
                                            : (e.target.value as number),
                                    )
                                }
                                label="Select Name Column"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {headers.map((header, index) => (
                                    <MenuItem key={index} value={index}>
                                        {header || `Column ${index + 1}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {/* Other fields */}
                {(['email', 'phone', 'linkedinProfile'] as const).map(
                    (field) => {
                        const fieldLabels: Record<
                            keyof ColumnMapping,
                            string
                        > = {
                            name: 'Name',
                            email: 'Email',
                            phone: 'Phone',
                            linkedinProfile: 'LinkedIn Profile',
                            first_name_header: 'First Name',
                            last_name_header: 'Last Name',
                        }

                        return (
                            <Box key={field} className={classes.mappingRow}>
                                <Typography className={classes.mappingLabel}>
                                    {fieldLabels[field]}:
                                </Typography>
                                <FormControl
                                    variant="outlined"
                                    style={{ minWidth: 250 }}
                                >
                                    <InputLabel>
                                        Select {fieldLabels[field]} Column
                                    </InputLabel>
                                    <Select
                                        value={
                                            columnMapping[field] !== null
                                                ? columnMapping[field]
                                                : ''
                                        }
                                        onChange={(e) =>
                                            handleColumnMappingChange(
                                                field,
                                                e.target.value === ''
                                                    ? null
                                                    : (e.target
                                                          .value as number),
                                            )
                                        }
                                        label={`Select ${fieldLabels[field]} Column`}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {headers.map((header, index) => (
                                            <MenuItem key={index} value={index}>
                                                {header ||
                                                    `Column ${index + 1}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )
                    },
                )}
            </Box>
        )
    }

    const renderConfirmationStep = () => {
        const headers = getHeaders()
        const previewRows = getPreviewRows()

        // Get all mapped column indices
        const mappedIndices = new Set<number>()
        if (columnMapping.name !== null) mappedIndices.add(columnMapping.name)
        if (columnMapping.first_name_header !== null)
            mappedIndices.add(columnMapping.first_name_header)
        if (columnMapping.last_name_header !== null)
            mappedIndices.add(columnMapping.last_name_header)
        if (columnMapping.email !== null) mappedIndices.add(columnMapping.email)
        if (columnMapping.phone !== null) mappedIndices.add(columnMapping.phone)
        if (columnMapping.linkedinProfile !== null)
            mappedIndices.add(columnMapping.linkedinProfile)

        // Get custom columns (unmapped columns)
        const customColumns = headers
            .map((header, index) => ({ header, index }))
            .filter(({ index }) => !mappedIndices.has(index))

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Confirm Mapping
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Review your column mappings and preview how the data will be
                    imported.
                </Typography>

                <Box style={{ marginBottom: 24 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Column Mappings:
                    </Typography>
                    {/* Name mapping - handle split or single */}
                    {columnMapping.first_name_header !== null ||
                    columnMapping.last_name_header !== null ? (
                        <>
                            <Box
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{ minWidth: 150 }}
                                >
                                    First Name:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {columnMapping.first_name_header !== null
                                        ? headers[
                                              columnMapping.first_name_header
                                          ] ||
                                          `Column ${
                                              columnMapping.first_name_header +
                                              1
                                          }`
                                        : 'Not mapped'}
                                </Typography>
                            </Box>
                            <Box
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{ minWidth: 150 }}
                                >
                                    Last Name:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {columnMapping.last_name_header !== null
                                        ? headers[
                                              columnMapping.last_name_header
                                          ] ||
                                          `Column ${
                                              columnMapping.last_name_header + 1
                                          }`
                                        : 'Not mapped'}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Box
                            style={{
                                display: 'flex',
                                gap: 8,
                                marginBottom: 8,
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{ minWidth: 150 }}
                            >
                                Name:
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {columnMapping.name !== null
                                    ? headers[columnMapping.name] ||
                                      `Column ${columnMapping.name + 1}`
                                    : 'Not mapped'}
                            </Typography>
                        </Box>
                    )}
                    {/* Other fields */}
                    {(['email', 'phone', 'linkedinProfile'] as const).map(
                        (field) => {
                            const fieldLabels: Record<string, string> = {
                                email: 'Email',
                                phone: 'Phone',
                                linkedinProfile: 'LinkedIn Profile',
                            }
                            const columnIndex = columnMapping[field]
                            return (
                                <Box
                                    key={field}
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        marginBottom: 8,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        style={{ minWidth: 150 }}
                                    >
                                        {fieldLabels[field]}:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {columnIndex !== null
                                            ? headers[columnIndex] ||
                                              `Column ${columnIndex + 1}`
                                            : 'Not mapped'}
                                    </Typography>
                                </Box>
                            )
                        },
                    )}
                    {/* Custom columns info */}
                    {customColumns.length > 0 && (
                        <Box
                            style={{
                                marginTop: 16,
                                padding: 12,
                                backgroundColor: '#f0f9ff',
                                borderRadius: 8,
                                border: '1px solid #bae6fd',
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                style={{ marginBottom: 8, fontWeight: 600 }}
                            >
                                Custom Columns ({customColumns.length}):
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                The following columns will be stored as custom
                                data:{' '}
                                {customColumns
                                    .map(
                                        ({ header, index }) =>
                                            header || `Column ${index + 1}`,
                                    )
                                    .join(', ')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                    Data Preview:
                </Typography>
                <TableContainer
                    component={Paper}
                    className={classes.previewTable}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeader}>
                                    Name
                                </TableCell>
                                <TableCell className={classes.tableHeader}>
                                    Email
                                </TableCell>
                                <TableCell className={classes.tableHeader}>
                                    Phone
                                </TableCell>
                                <TableCell className={classes.tableHeader}>
                                    LinkedIn Profile
                                </TableCell>
                                {/* Custom columns */}
                                {customColumns.map(({ header, index }) => (
                                    <TableCell
                                        key={index}
                                        className={classes.tableHeader}
                                    >
                                        {header || `Column ${index + 1}`}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {previewRows.map((row, rowIndex) => {
                                // Build name from either single column or split columns
                                let nameValue = ''
                                if (
                                    columnMapping.first_name_header !== null ||
                                    columnMapping.last_name_header !== null
                                ) {
                                    const firstName =
                                        columnMapping.first_name_header !== null
                                            ? row[
                                                  columnMapping
                                                      .first_name_header
                                              ] || ''
                                            : ''
                                    const lastName =
                                        columnMapping.last_name_header !== null
                                            ? row[
                                                  columnMapping.last_name_header
                                              ] || ''
                                            : ''
                                    nameValue = `${firstName} ${lastName}`.trim()
                                } else if (columnMapping.name !== null) {
                                    nameValue = row[columnMapping.name] || ''
                                }

                                return (
                                    <TableRow key={rowIndex}>
                                        <TableCell>{nameValue}</TableCell>
                                        <TableCell>
                                            {columnMapping.email !== null
                                                ? row[columnMapping.email] || ''
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            {columnMapping.phone !== null
                                                ? row[columnMapping.phone] || ''
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            {columnMapping.linkedinProfile !==
                                            null
                                                ? row[
                                                      columnMapping
                                                          .linkedinProfile
                                                  ] || ''
                                                : ''}
                                        </TableCell>
                                        {/* Custom column data */}
                                        {customColumns.map(({ index }) => (
                                            <TableCell key={index}>
                                                {row[index] || ''}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        )
    }

    const renderStepContent = () => {
        switch (steps[activeStep]) {
            case 'upload':
                return renderUploadStep()
            case 'header':
                return renderHeaderStep()
            case 'mapping':
                return renderMappingStep()
            case 'confirmation':
                return renderConfirmationStep()
            default:
                return null
        }
    }

    const canProceed = () => {
        switch (steps[activeStep]) {
            case 'upload':
                return uploadedFile !== null && !loading && !error
            case 'header':
                return parsedData.length > 0
            case 'mapping':
                return true // No fields are required
            case 'confirmation':
                return true
            default:
                return false
        }
    }

    const getStepLabel = (step: UploadStep): string => {
        switch (step) {
            case 'upload':
                return 'Upload File'
            case 'header':
                return 'Select Headers'
            case 'mapping':
                return 'Map Columns'
            case 'confirmation':
                return 'Confirm'
            default:
                return ''
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <Box>
                    <Typography className={classes.title}>
                        Upload Prospects Spreadsheet
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Import targets and prospects into your leasing season
                        pool. Rows with contact info become prospects; others
                        stay targets for enrichment.
                    </Typography>
                </Box>
                <Link
                    to={Routes.manage.leasingSeason.root}
                    style={{ textDecoration: 'none' }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ArrowLeft />}
                        className={classes.backButton}
                    >
                        Back to Prospects
                    </Button>
                </Link>
            </Box>

            <Card>
                <CardContent>
                    <Stepper
                        activeStep={activeStep}
                        className={classes.stepper}
                    >
                        {steps.map((step, index) => (
                            <Step key={step}>
                                <StepLabel>{getStepLabel(step)}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Box className={classes.stepContent}>
                        {renderStepContent()}
                    </Box>

                    <Box className={classes.actionsContainer}>
                        <Button
                            onClick={handleBack}
                            disabled={activeStep === 0 || loading}
                            variant="outlined"
                            className={classes.backButton}
                        >
                            Back
                        </Button>
                        {activeStep === steps.length - 1 ? (
                            <Button
                                onClick={handleUpload}
                                disabled={!canProceed() || loading}
                                className={classes.gradientButton}
                                startIcon={
                                    loading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <CheckCircle />
                                    )
                                }
                            >
                                {loading ? 'Uploading...' : 'Upload & Import'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                disabled={!canProceed()}
                                className={classes.gradientButton}
                            >
                                Next
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}

export default SpreadsheetUploadPage
